import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

const InstagramContext = createContext();

export const useInstagram = () => useContext(InstagramContext);

const INSTAGRAM_APP_ID = import.meta.env.VITE_INSTAGRAM_APP_ID;
const INSTAGRAM_REDIRECT_URI = import.meta.env.VITE_INSTAGRAM_REDIRECT_URI;
const FACEBOOK_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID;
const FACEBOOK_REDIRECT_URI = import.meta.env.VITE_FACEBOOK_REDIRECT_URI;
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';

// Instagram Business Login scopes (only instagram_business_* scopes allowed on instagram.com/oauth/authorize)
const SCOPES = [
  'instagram_business_basic',
  'instagram_business_manage_insights',
  'instagram_business_content_publish',
  'instagram_business_manage_comments',
  'instagram_business_manage_messages',
].join(',');

// Generate a cryptographically secure random string for CSRF state
const generateRandomString = (length = 32) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
    .substring(0, length);
};

export const InstagramProvider = ({ children }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const user = useSelector((state) => state.auth?.user);

  // Load connected accounts from backend on mount
  useEffect(() => {
    if (user) {
      loadConnectedAccounts();
    } else {
      setAccounts([]);
      setLoading(false);
    }
  }, [user]);

  const loadConnectedAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/instagram/accounts`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      }
    } catch (err) {
      console.error('Failed to load Instagram accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Start Instagram OAuth flow (via Facebook Login)
  const connectInstagram = useCallback(() => {
    try {
      // Generate CSRF state token with ig_ prefix for MetaCallback routing
      const state = `ig_${generateRandomString(32)}`;
      sessionStorage.setItem('instagram_oauth_state', state);

      // Build Instagram Business Login authorization URL
      const authUrl = new URL('https://www.instagram.com/oauth/authorize');
      authUrl.searchParams.set('client_id', INSTAGRAM_APP_ID);
      authUrl.searchParams.set('redirect_uri', INSTAGRAM_REDIRECT_URI);
      authUrl.searchParams.set('scope', SCOPES);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('state', state);
      authUrl.searchParams.set('enable_fb_login', '0');
      authUrl.searchParams.set('force_authentication', '0');

      // Redirect to Facebook
      window.location.href = authUrl.toString();
    } catch (err) {
      console.error('Failed to start Instagram OAuth:', err);
      setError('Failed to start Instagram connection');
    }
  }, []);

  // Connect Instagram THROUGH Facebook Login.
  //
  // The user is still connecting Instagram -- Facebook is only who authorises
  // it. That distinction matters in the UI: this must never be labelled as
  // "connect Facebook", which is a separate feature on the same screen.
  //
  // Why it exists: per-post reach, impressions and saves, and Story insights,
  // are only readable with a Page token. Connecting this way gets one up front,
  // so no upgrade step is ever needed. Requires a Business/Creator account
  // linked to a Page the user manages; connectInstagram is the fallback when
  // that is not true.
  const connectInstagramViaFacebook = useCallback(() => {
    try {
      // `igfb_` so MetaCallback and handleCallback route to the right exchange.
      const state = `igfb_${generateRandomString(32)}`;
      sessionStorage.setItem('instagram_oauth_state', state);

      const scopes = [
        'instagram_basic',
        'instagram_manage_insights',
        'instagram_manage_comments',
        'pages_show_list',
        'pages_read_engagement',
        'business_management',
      ].join(',');

      const authUrl = new URL('https://www.facebook.com/v21.0/dialog/oauth');
      authUrl.searchParams.set('client_id', FACEBOOK_APP_ID);
      authUrl.searchParams.set('redirect_uri', FACEBOOK_REDIRECT_URI);
      authUrl.searchParams.set('scope', scopes);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('state', state);

      window.location.href = authUrl.toString();
    } catch (err) {
      console.error('Failed to start Instagram (via Facebook) OAuth:', err);
      setError('Failed to start Instagram connection');
    }
  }, []);

  // Handle OAuth callback - Step 1: exchange code and discover accounts
  const handleCallback = useCallback(async (code, state) => {
    // Validate state parameter
    const savedState = sessionStorage.getItem('instagram_oauth_state');
    if (!savedState || state !== savedState) {
      throw new Error('Invalid state parameter - possible CSRF attack');
    }

    // `igfb_` marks a connection started through Facebook Login. It exchanges
    // at a different endpoint with a different redirect URI, because it is a
    // different provider -- but both return the same {available_accounts,
    // token_ref}, so everything downstream is identical.
    const viaFacebook = state.startsWith('igfb_');
    const endpoint = viaFacebook
      ? `${API_BASE_URL}/instagram/oauth/fb-callback`
      : `${API_BASE_URL}/instagram/oauth/callback`;
    const redirectUri = viaFacebook ? FACEBOOK_REDIRECT_URI : INSTAGRAM_REDIRECT_URI;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code,
          redirect_uri: redirectUri,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to connect Instagram account');
      }

      const data = await response.json();

      // Clean up state
      sessionStorage.removeItem('instagram_oauth_state');

      // Return available accounts and token_ref for selection step
      return {
        availableAccounts: data.available_accounts,
        tokenRef: data.token_ref,
      };
    } catch (err) {
      sessionStorage.removeItem('instagram_oauth_state');
      throw err;
    }
  }, []);

  // Step 2: Select which Instagram account to connect
  const selectAccount = useCallback(async (tokenRef, pageId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/instagram/oauth/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token_ref: tokenRef,
          page_id: pageId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to select Instagram account');
      }

      const data = await response.json();

      // Dedupe by id: re-connecting an account already linked to this org returns
      // the SAME account (already_connected=true), not a new one — so update in
      // place instead of appending a duplicate.
      setAccounts(prev => {
        const exists = prev.some(a => a.id === data.account.id);
        return exists
          ? prev.map(a => (a.id === data.account.id ? data.account : a))
          : [...prev, data.account];
      });

      return { account: data.account, alreadyConnected: !!data.already_connected };
    } catch (err) {
      console.error('Failed to select Instagram account:', err);
      throw err;
    }
  }, []);

  // Disconnect an account
  const disconnectAccount = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setAccounts(prev => prev.filter(a => a.id !== accountId));
      } else {
        throw new Error('Failed to disconnect account');
      }
    } catch (err) {
      console.error('Failed to disconnect Instagram account:', err);
      setError(err.message);
    }
  }, []);

  // Refresh account stats
  const refreshAccountStats = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAccounts(prev =>
          prev.map(a => (a.id === accountId ? { ...a, ...data.account } : a))
        );
        return data.account;
      } else {
        throw new Error('Failed to refresh stats');
      }
    } catch (err) {
      console.error('Failed to refresh Instagram stats:', err);
      setError(err.message);
    }
  }, []);

  // Fetch media for an account
  const fetchAccountMedia = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/media`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch media');
      }
    } catch (err) {
      console.error('Failed to fetch Instagram media:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Fetch insights for an account
  const fetchInsights = useCallback(async (accountId, period = 'day') => {
    try {
      const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/insights?period=${period}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch insights');
      }
      return await response.json();
    } catch (err) {
      console.error('Failed to fetch Instagram insights:', err);
      throw err;
    }
  }, []);

  // Fetch comments for a media item
  const fetchMediaComments = useCallback(async (accountId, mediaId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/media/${mediaId}/comments`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch comments');
      }
      return await response.json();
    } catch (err) {
      console.error('Failed to fetch comments:', err);
      throw err;
    }
  }, []);

  // Reply to a comment
  const replyToComment = useCallback(async (accountId, mediaId, commentId, message) => {
    try {
      const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/media/${mediaId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ comment_id: commentId, message }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reply');
      }
      return await response.json();
    } catch (err) {
      console.error('Failed to reply to comment:', err);
      throw err;
    }
  }, []);

  // Delete a comment
  const deleteComment = useCallback(async (accountId, commentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/comments/${commentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete comment');
      }
      return await response.json();
    } catch (err) {
      console.error('Failed to delete comment:', err);
      throw err;
    }
  }, []);

  // Create a post on Instagram
  const createPost = useCallback(async (accountId, { image_url, caption, media_type, media_file_ids, carousel_urls }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_url, caption, media_type, media_file_ids, carousel_urls }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create post');
      }

      return await response.json();
    } catch (err) {
      console.error('Failed to create Instagram post:', err);
      throw err;
    }
  }, []);

  // Start Facebook Login OAuth to upgrade an account.
  //
  // This grants two things at once, and the name is historical: comments
  // access, and the full metrics set. Per-post reach, impressions and saves —
  // and Story insights — are only readable through Facebook Login with the
  // account linked to a Page. An Instagram-Login token reaches profile, media
  // and a narrower insights set, which is why an un-upgraded account shows
  // empty columns on the analytics page.
  const upgradeForComments = useCallback((accountId) => {
    const state = `igupgrade_${accountId}_${generateRandomString(16)}`;
    sessionStorage.setItem('ig_upgrade_state', state);
    sessionStorage.setItem('ig_upgrade_account_id', accountId);

    const scopes = [
      'instagram_manage_comments',
      // Without instagram_manage_insights the upgrade returns a Page token
      // that still cannot read per-post insights -- the exact metrics this
      // upgrade exists to unlock.
      'instagram_manage_insights',
      'instagram_basic',
      'pages_show_list',
      'pages_read_engagement',
      'business_management',
    ].join(',');

    const authUrl = new URL('https://www.facebook.com/v21.0/dialog/oauth');
    authUrl.searchParams.set('client_id', FACEBOOK_APP_ID);
    authUrl.searchParams.set('redirect_uri', FACEBOOK_REDIRECT_URI);
    authUrl.searchParams.set('scope', scopes);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('state', state);

    window.location.href = authUrl.toString();
  }, []);

  // Handle the callback from Facebook Login upgrade
  const handleUpgradeCallback = useCallback(async (code, state) => {
    const savedState = sessionStorage.getItem('ig_upgrade_state');
    const accountId = sessionStorage.getItem('ig_upgrade_account_id');

    if (!savedState || state !== savedState) {
      throw new Error('Invalid state parameter');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/upgrade-comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code,
          redirect_uri: FACEBOOK_REDIRECT_URI,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upgrade comments access');
      }

      const data = await response.json();

      // Update the account in state
      setAccounts(prev =>
        prev.map(a => (a.id === accountId ? { ...a, ...data.account } : a))
      );

      return data;
    } finally {
      sessionStorage.removeItem('ig_upgrade_state');
      sessionStorage.removeItem('ig_upgrade_account_id');
    }
  }, []);

  // Get account by ID
  const getAccountById = useCallback((accountId) => {
    return accounts.find(a => a.id === accountId);
  }, [accounts]);

  // --- Messaging ---
  const fetchConversations = useCallback(async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/conversations`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch conversations');
    }
    return response.json();
  }, []);

  const fetchMessages = useCallback(async (accountId, conversationId) => {
    const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/conversations/${conversationId}/messages`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch messages');
    }
    return response.json();
  }, []);

  const sendMessage = useCallback(async (accountId, recipientId, text) => {
    const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/messages/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ recipient_id: recipientId, text }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send message');
    }
    return response.json();
  }, []);

  // --- Auto-Reply Rules ---
  const fetchAutoReplyRules = useCallback(async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/auto-replies`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch auto-reply rules');
    }
    return response.json();
  }, []);

  const createAutoReplyRule = useCallback(async (accountId, { keyword, reply_text, reply_type, media_id }) => {
    const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/auto-replies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ keyword, reply_text, reply_type, media_id }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create auto-reply rule');
    }
    return response.json();
  }, []);

  const deleteAutoReplyRule = useCallback(async (accountId, ruleId) => {
    const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/auto-replies/${ruleId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete rule');
    }
    return response.json();
  }, []);

  const toggleAutoReplyRule = useCallback(async (accountId, ruleId, isActive) => {
    const response = await fetch(`${API_BASE_URL}/instagram/accounts/${accountId}/auto-replies/${ruleId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ is_active: isActive }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update rule');
    }
    return response.json();
  }, []);

  return (
    <InstagramContext.Provider
      value={{
        accounts,
        loading,
        error,
        connectInstagram,
        connectInstagramViaFacebook,
        handleCallback,
        selectAccount,
        disconnectAccount,
        refreshAccountStats,
        loadConnectedAccounts,
        fetchAccountMedia,
        fetchInsights,
        fetchMediaComments,
        replyToComment,
        deleteComment,
        createPost,
        getAccountById,
        upgradeForComments,
        // Same flow, named for what it unlocks. `upgradeForComments` predates
        // the metrics use and is kept so existing callers keep working.
        upgradeForFullMetrics: upgradeForComments,
        handleUpgradeCallback,
        fetchConversations,
        fetchMessages,
        sendMessage,
        fetchAutoReplyRules,
        createAutoReplyRule,
        deleteAutoReplyRule,
        toggleAutoReplyRule,
      }}
    >
      {children}
    </InstagramContext.Provider>
  );
};

export default InstagramContext;
